import { useMemo, useState } from "react";
import { Sparkles, Users, ChevronDown, Check, Search, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { krestni } from "@/lib/odkazy";

// 268: kdo odkaz na dokument uvidí + jestli ho smí otevřít AI asistent.
// 315: rozklikávací tlačítko místo řady jmen; skupiny podle pozice (platí i pro nové lidi)
// a konkrétní lidé. Prázdné skupiny i lidé = každý, kdo vidí zakázku (majitel a autor vždy).
// Schovává se jen v Proyektech — přístup k souborům dál hlídá disk firmy.

export const SKUPINY = [
  { key: "vedeni", label: "Vedení", sub: "majitel, stavbyvedoucí, asistentka", role: ["OWNER", "MANAGER", "ASSISTANT"] },
  { key: "projektant", label: "Projektanti", role: ["DESIGNER"] },
  { key: "delnici", label: "Dělníci", sub: "i subdodavatelé", role: ["WORKER", "SUBCONTRACTOR"] },
];
export const VYCHOZI_SKUPINY = ["vedeni"];
const ROLE = { MANAGER: "Stavbyvedoucí", ASSISTANT: "Asistentka", DESIGNER: "Projektant", WORKER: "Dělník", SUBCONTRACTOR: "Subdodavatel",
  WAREHOUSE: "Skladník", ACCOUNTANT: "Účetní", OWNER: "Majitel" };
const norm = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Krátký popis výběru: „Všichni, kdo vidí zakázku“, „Vedení“, „Vedení + Martin“, „Vedení, Dělníci + 3 lidé“. */
export function popisKdo(komu = [], skupiny = [], team = []) {
  if (!komu.length && !skupiny.length) return "Všichni, kdo vidí zakázku";
  const sk = SKUPINY.filter((s) => skupiny.includes(s.key)).map((s) => s.label);
  const lide = komu.map((id) => krestni(team.find((m) => m.id === id)?.name || "")).filter(Boolean);
  const lidiText = komu.length > 2 ? `${komu.length} ${komu.length < 5 ? "lidé" : "lidí"}` : lide.join(", ") || `${komu.length} ${komu.length === 1 ? "člověk" : "lidé"}`;
  if (!sk.length) return `Jen ${lidiText}`;
  return komu.length ? `${sk.join(", ")} + ${lidiText}` : sk.join(", ");
}

const Box = ({ on, pres }) => (
  <span className={`w-[18px] h-[18px] shrink-0 rounded-[5px] border-[1.5px] grid place-items-center ${on ? "bg-brand border-brand text-white" : pres ? "bg-secondary border-border text-muted-foreground" : "bg-card border-border"}`}>
    {(on || pres) && <Check size={12} strokeWidth={3} />}
  </span>
);

export default function KdoUvidi({ team = [], meId, komu = [], onKomu, skupiny = [], onSkupiny, ai, onAi, testid = "kdo-uvidi" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const vsichni = !komu.length && !skupiny.length;
  const lide = useMemo(() => team.filter((m) => m.id !== meId && m.role !== "OWNER"), [team, meId]);
  const shown = q ? lide.filter((m) => norm(`${m.name} ${ROLE[m.role] || ""}`).includes(norm(q))) : lide;
  // Poslední zaškrtnutí nejde zrušit — prázdný výběr by znamenal „všichni“ (to je samostatná volba nahoře).
  const posledni = komu.length + skupiny.length === 1;
  const prepniSk = (k) => { if (skupiny.includes(k)) { if (!posledni) onSkupiny(skupiny.filter((x) => x !== k)); } else onSkupiny([...skupiny, k]); };
  const prepniClo = (id) => { if (komu.includes(id)) { if (!posledni) onKomu(komu.filter((x) => x !== id)); } else onKomu([...komu, id]); };
  const radek = "w-full min-h-[44px] px-3 flex items-center gap-2.5 text-left rounded-[10px] hover:bg-secondary transition-colors motion-reduce:transition-none";
  const nadpis = "px-3 pt-2 pb-1 text-[11px] font-bold tracking-[.1em] uppercase text-muted-foreground";

  return (
    <div className="space-y-3" data-testid={testid}>
      <div>
        <div className="text-[12px] font-bold uppercase tracking-[.06em] text-muted-foreground mb-1.5">Kdo uvidí</div>
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`${testid}-tlacitko`}
          className="h-11 w-full md:w-auto md:min-w-[260px] max-w-full pl-3 pr-3.5 rounded-full border border-border bg-card flex items-center gap-2.5 text-sm font-semibold hover:bg-secondary">
          <Users size={16} className="text-muted-foreground shrink-0" />
          <span className="flex-1 min-w-0 truncate text-left" data-testid={`${testid}-popis`}>{popisKdo(komu, skupiny, team)}</span>
          <ChevronDown size={15} className={`text-muted-foreground shrink-0 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="mt-2 bg-card border border-border rounded-[16px] shadow-[0_16px_48px_rgba(0,0,0,.14)] overflow-hidden" data-testid={`${testid}-panel`}>
            <div className="p-1.5">
              <button type="button" onClick={() => { onKomu([]); onSkupiny([]); }} aria-pressed={vsichni} className={radek} data-testid={`${testid}-vsichni`}>
                <Box on={vsichni} />
                <span className="min-w-0"><span className="block text-[14px] font-semibold">Všichni, kdo vidí zakázku</span>
                  <span className="block text-[12px] text-muted-foreground">i dělníci a subdodavatelé na zakázce</span></span>
              </button>
              <div className={nadpis}>Skupiny</div>
              {SKUPINY.map((s) => {
                const on = skupiny.includes(s.key);
                return (
                  <button key={s.key} type="button" onClick={() => prepniSk(s.key)} aria-pressed={on} className={radek} data-testid={`${testid}-sk-${s.key}`}
                    title={on && posledni ? "Aspoň jedna skupina nebo člověk — jinak zvolte Všichni" : undefined}>
                    <Box on={on} />
                    <span className="min-w-0"><span className="block text-[14px] font-semibold">{s.label}</span>
                      {s.sub && <span className="block text-[12px] text-muted-foreground">{s.sub}</span>}</span>
                  </button>
                );
              })}
              {lide.length > 0 && <div className={nadpis}>Konkrétní lidé</div>}
              {lide.length > 6 && (
                <div className="px-1.5 pb-1">
                  <div className="h-10 rounded-[10px] bg-secondary/70 px-3 flex items-center gap-2">
                    <Search size={15} className="text-muted-foreground shrink-0" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat jméno…" aria-label="Hledat člověka" data-testid={`${testid}-hledat`}
                      className="bg-transparent outline-none w-full min-w-0 text-base md:text-sm" />
                    {q && <button type="button" onClick={() => setQ("")} aria-label="Smazat hledání" className="text-muted-foreground"><X size={15} /></button>}
                  </div>
                </div>
              )}
              <div className="max-h-[220px] overflow-y-auto overscroll-contain">
                {shown.map((m) => {
                  const on = komu.includes(m.id);
                  const pres = SKUPINY.find((x) => skupiny.includes(x.key) && x.role.includes(m.role));
                  return (
                    <button key={m.id} type="button" onClick={() => prepniClo(m.id)} aria-pressed={on} className={`${radek} ${on ? "bg-brand/[.07]" : ""}`} data-testid={`${testid}-${m.id}`}>
                      <Box on={on} pres={!!pres} />
                      <span className="min-w-0 flex-1 truncate text-[14px] font-semibold">{m.name} <span className="font-normal text-[12.5px] text-muted-foreground">· {pres && !on ? `vidí přes ${pres.label}` : ROLE[m.role] || m.role}</span></span>
                    </button>
                  );
                })}
                {q && !shown.length && <p className="px-3 py-2.5 text-[13px] text-muted-foreground">Nikdo takový v týmu není.</p>}
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2.5 border-t border-border">
              <span className="text-[12.5px] text-muted-foreground min-w-0 truncate">{vsichni ? "Uvidí každý na zakázce" : "Ty a majitel ho vidíte vždy"}</span>
              <button type="button" onClick={() => setOpen(false)} className="ml-auto h-9 px-4 rounded-full bg-brand text-white text-[13px] font-bold shrink-0" data-testid={`${testid}-hotovo`}>Hotovo</button>
            </div>
          </div>
        )}
        {!open && (
          <p className="text-[12.5px] text-muted-foreground mt-1.5">
            {vsichni ? "Kdo vidí zakázku, uvidí i odkaz — i dělníci." : "Jen vybraní — ty a majitel ho vidíte vždy."}
          </p>
        )}
      </div>
      <label className="flex items-center gap-3 rounded-[14px] bg-secondary px-3.5 py-2.5 cursor-pointer">
        <Sparkles size={17} className="text-brand shrink-0" />
        <span className="flex-1 min-w-0">
          <span className="block text-[14px] md:text-[13.5px] font-bold">AI asistent smí otevřít</span>
          <span className="block text-[12.5px] text-muted-foreground">Claude nebo ChatGPT připojený k Proyektům — soubory čte pod účtem toho, kdo se ptá.</span>
        </span>
        <Switch checked={!!ai} onCheckedChange={onAi} aria-label="AI asistent smí otevřít" data-testid={`${testid}-ai`} />
      </label>
    </div>
  );
}

/** Drobný popisek do řádku: komu je odkaz vidět a jestli ho smí AI. */
export function PopisViditelnosti({ komu = [], skupiny = [], ai, team = [] }) {
  const omezeno = komu.length > 0 || skupiny.length > 0;
  const text = omezeno ? popisKdo(komu, skupiny, team) : "";
  return (
    <>
      {omezeno && <span title={text}> · {text.startsWith("Jen ") ? text.charAt(0).toLowerCase() + text.slice(1) : `jen ${text.charAt(0).toLowerCase() + text.slice(1)}`}</span>}
      {ai && <> · <span className="inline-flex items-center gap-0.5 text-brand" title="AI asistent smí otevřít"><Sparkles size={11} /> AI</span></>}
    </>
  );
}
